"use client";

import classNames from "classnames/bind";
import styles from "./FeatureStackingCards.module.css";
import { Prose } from "../Prose/Prose";
import { VerticalNavDots } from "../VerticalNavDots/VerticalNavDots";
import { CardText } from "./CardText";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useEffect, useCallback, useRef } from "react";
import pixels from "./pixels.png";
import { useState } from "react";
import Image from "next/image";
import RivePlayer, { RivePlayerRef } from "../RivePlayer/RivePlayer";
import type { PayloadImageGroup } from "@/helpers/imageHelper";
import { getImage } from "@/helpers/imageHelper";
import type { Media } from "@/payload-types";
import { CarouselButton } from "../CarouselButton/CarouselButton";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const cx = classNames.bind(styles);

export interface CardProps {
  kicker: string;
  title: string;
  text: string;
  image?: PayloadImageGroup["file"];
  rive?: {
    file: Media | string;
    animation: string;
  };
  mobileRive?: {
    file: Media | string;
    animation: string;
  };
  mobileImage?: PayloadImageGroup["file"];
  fullscreen?: boolean;
}

// Helper function to get Rive file URL
const getRiveFileUrl = (riveFile: Media | string | undefined): string => {
  if (!riveFile) return "/rive/250527_NLX_Web_MM_01.riv";
  return typeof riveFile === "string"
    ? riveFile
    : riveFile.url || "/rive/250527_NLX_Web_MM_01.riv";
};

export default function FeatureStackingCards({
  className,
  title,
  text,
  cards,
}: {
  className?: string;
  title: string;
  text: string | React.ReactNode;
  cards: CardProps[];
}) {
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const container = useRef<HTMLDivElement>(null);
  const cardsContainer = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const cardsMobileRef = useRef<HTMLDivElement[]>([]);
  const cardsDecorativeRef = useRef<HTMLDivElement[]>([]);
  const riveRefs = useRef<(RivePlayerRef | null)[]>([]);
  const riveMobileRefs = useRef<(RivePlayerRef | null)[]>([]);

  const getCombinedRefs = (): (HTMLDivElement | null)[] => [
    ...cardsDecorativeRef.current,
    ...cardsRef.current,
  ];
  const CARD_Y = 20;
  const CARD_Z = 20;

  // Next button handler
  const next = useCallback(() => {
    const newActiveIndex = activeIndex + 1;

    if (newActiveIndex < cards.length) {
      setActiveIndex(newActiveIndex);
    }

    // Loop through all cards and set CSS props
    for (let i = getCombinedRefs().length - 1; i >= 0; i--) {
      if (getCombinedRefs()[i]) {
        getCombinedRefs()[i]!.dataset.currentLevel = String(
          i - (newActiveIndex + 2),
        );
        getCombinedRefs()[i]!.dataset.position =
          i - (newActiveIndex + 2) === 0
            ? "active"
            : i - (newActiveIndex + 2) < 0
              ? "below"
              : "above";
        getCombinedRefs()[i]!.dataset.hidden =
          newActiveIndex + 2 - i > 2 ? "true" : undefined;

        gsap.to(getCombinedRefs()[i], {
          "--y": `${CARD_Y * (i - (newActiveIndex + 2))}px`,
          "--z": `${CARD_Z * (i - (newActiveIndex + 2))}px`,
          "--opacity": i - (newActiveIndex + 2) <= 0 ? 1 : 0,
          ease: "none",
        });
      }
    }
  }, [activeIndex, cards.length]);

  // Previous button handler
  const prev = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }

    const newActiveIndex = activeIndex - 1;

    // Loop through all cards and set CSS props
    for (let i = getCombinedRefs().length - 1; i >= 0; i--) {
      if (getCombinedRefs()[i]) {
        getCombinedRefs()[i]!.dataset.currentLevel = String(
          i - (newActiveIndex + 2),
        );
        getCombinedRefs()[i]!.dataset.position =
          i - (newActiveIndex + 2) === 0
            ? "active"
            : i - (newActiveIndex + 2) < 0
              ? "below"
              : "above";
        getCombinedRefs()[i]!.dataset.hidden =
          newActiveIndex + 2 - i > 2 ? "true" : undefined;

        gsap.to(getCombinedRefs()[i], {
          "--y": `${CARD_Y * (i - (newActiveIndex + 2))}px`,
          "--z": `${CARD_Z * (i - (newActiveIndex + 2))}px`,
          "--opacity": i - (newActiveIndex + 2) <= 0 ? 1 : 0,
          ease: "none",
        });
      }
    }
  }, [activeIndex]);

  useGSAP(() => {
    // Prevent animation setup if window width is 768px or less
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return;
    }

    // Cards are all stacked on top of each other via a CSS Grid
    // This assumes Cards are not visible on page load...
    cardsRef.current.forEach((card, index) => {
      if (!card) return;
      gsap.to(card, {
        "--opacity": index === 0 ? 1 : 0,
        "--y": index === 0 ? 0 : `${CARD_Y}px`,
        "--z": index === 0 ? 0 : `${CARD_Z}px`,
      });
    });

    // Set a CSS prop for a scroll in animation
    gsap.set(getCombinedRefs()[0], {
      attr: { ["data-start"]: "true" },
    });
    gsap.set(getCombinedRefs()[1], {
      attr: { ["data-start"]: "true" },
    });
    gsap.set(getCombinedRefs()[2], {
      attr: { ["data-start"]: "true" },
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        markers: false,
        trigger: container.current,

        start: "top center",

        // Play the first Rive animation
        onEnter: () => {
          setActiveIndex(0);
        },
      },
    });

    // Trigger an animation where the components expand as we scroll into view
    tl.to(getCombinedRefs()[0], { attr: { ["data-start"]: "false" } })
      .to(getCombinedRefs()[1], { attr: { ["data-start"]: "false" } }, "<")
      .to(getCombinedRefs()[2], { attr: { ["data-start"]: "false" } }, "<");
  });

  useGSAP(() => {
    // Prevent animation setup if window width is bigger than 768px
    if (typeof window !== "undefined" && window.innerWidth > 768) {
      return;
    }

    cardsMobileRef.current.forEach((card, index) => {
      if (!card) return;

      ScrollTrigger.create({
        trigger: card,
        start: "top-=50% top",
        end: "bottom bottom",
        scrub: true,
        markers: false,
        onEnter: () => {
          riveMobileRefs.current[index]?.play();
        },
      });
    });
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const activeRive = riveRefs.current[activeIndex];
      if (activeRive) {
        activeRive.restart();
      }
    }, 1000);
  }, [activeIndex]);

  return (
    <div>
      <div className={cx("container", className)} ref={container}>
        <div className={cx("inner")}>
          <div className={cx("header")}>
            <div className={cx("header-inner")}>
              <h2 className={cx("title")}>{title}</h2>
              <div>
                {text && (
                  <Prose size="base" className={cx("text")}>
                    {text}
                  </Prose>
                )}
                <div className={cx("carousel-nav")}>
                  <CarouselButton
                    kind="previous"
                    onClick={prev}
                    disabled={activeIndex <= 0}
                    className={cx("prev")}
                  />
                  <CarouselButton
                    kind="next"
                    onClick={next}
                    disabled={activeIndex === cards.length - 1}
                    className={cx("next")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* <div className={cx("spacer")} ref={spacerRef}></div> */}
          <div ref={cardsContainer}>
            <div className={cx("cards", "gsap-cards")}>
              <VerticalNavDots
                dotsCount={cards.length}
                activeIndex={activeIndex}
                className={cx("dots")}
              />
              {[...Array(2)].map((_, index) => (
                <div
                  key={index}
                  data-index={index}
                  data-current-level={`-${2 - index}`}
                  data-position="below"
                  className={cx("card", "gsap-card")}
                  ref={(el) => {
                    if (el && cardsDecorativeRef.current)
                      cardsDecorativeRef.current[index] = el;
                  }}
                >
                  {/* Decorative Pixel Overlay */}
                  <Image
                    className={cx("pixels")}
                    src={pixels.src}
                    alt=""
                    width={1429}
                    height={718}
                  />
                  <div className={cx("cards-inner")}></div>
                </div>
              ))}
              {cards.map((card, index) => {
                // Responsive main image from Payload
                const img = card.image
                  ? getImage(
                      { file: card.image },
                      "",
                      "card",
                      "(max-width: 900px) 100vw, 600px",
                    )
                  : null;
                return (
                  <div
                    key={index}
                    data-index={index + 2}
                    className={cx("card", "gsap-card")}
                    ref={(el) => {
                      if (el && cardsRef.current) cardsRef.current[index] = el;
                    }}
                  >
                    {/* Responsive Payload image */}
                    {img?.url && (
                      <Image
                        className={cx("card-image")}
                        src={img.url}
                        alt={img.alt ?? ""}
                        fill
                        sizes={img.sizes}
                        priority={index === 0}
                      />
                    )}

                    {/* Decorative pixel overlay */}
                    <Image
                      className={cx("pixels")}
                      src={pixels.src}
                      alt=""
                      width={1429}
                      height={718}
                    />

                    <div
                      className={
                        card.fullscreen
                          ? cx("cards-inner")
                          : cx("cards-inner_half")
                      }
                    >
                      <CardText
                        className={
                          card.fullscreen
                            ? cx("card-text")
                            : cx("card-text_half")
                        }
                        kicker={card.kicker}
                        title={card.title}
                        text={<p>{card.text}</p>}
                      />
                      {card.rive && (
                        <div
                          className={
                            card.fullscreen ? cx("media") : cx("media_half")
                          }
                        >
                          {/* Mobile Media */}
                          {(card.mobileRive || card.mobileImage) && (
                            <div className={cx("mobile-image")}>
                              {card.mobileRive ? (
                                <RivePlayer
                                  ref={(el) => {
                                    riveRefs.current[index] = el;
                                  }}
                                  src={getRiveFileUrl(card.mobileRive.file)}
                                  autoplay={false}
                                  animations={card.mobileRive.animation}
                                />
                              ) : card.mobileImage ? (
                                <Image
                                  src={
                                    getImage({ file: card.mobileImage }, "").url
                                  }
                                  alt=""
                                  fill
                                  sizes="(max-width: 1023px) 100vw, 50vw"
                                  priority={index === 0}
                                />
                              ) : null}
                            </div>
                          )}
                          {/* Rive Animation */}
                          <div className={cx("rive-container")}>
                            <RivePlayer
                              ref={(el) => {
                                riveRefs.current[index] = el;
                              }}
                              src={getRiveFileUrl(card.rive.file)}
                              autoplay={false}
                              animations={card.rive.animation}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={cx("cards-mobile")}>
              {cards.map((card, index) => {
                return (
                  <div
                    key={index}
                    className={cx("card-mobile")}
                    ref={(el) => {
                      if (el && cardsMobileRef.current)
                        cardsMobileRef.current[index] = el;
                    }}
                  >
                    <div className={cx("card-mobile-inner")}>
                      <CardText
                        className={cx("card-mobile-text")}
                        kicker={card.kicker}
                        title={card.title}
                        text={<p>{card.text}</p>}
                      />
                      {card.rive && (
                        <div className={cx("mobile-image")}>
                          {card.mobileRive ? (
                            <RivePlayer
                              ref={(el) => {
                                riveMobileRefs.current[index] = el;
                              }}
                              src={getRiveFileUrl(card.mobileRive.file)}
                              autoplay={false}
                              animations={card.mobileRive.animation}
                            />
                          ) : card.mobileImage ? (
                            <Image
                              src={getImage({ file: card.mobileImage }, "").url}
                              alt=""
                              fill
                              sizes="(max-width: 1023px) 100vw, 50vw"
                              priority={index === 0}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
